/**
 * Ed25519 adapter for Link-backed credentials.
 *
 * This file intentionally lives outside the model module. The model only knows that a credential protocol has
 * structural material and proof bytes; Ed25519 is one replaceable verification adapter over that structure.
 */

package com.oasisfeng.invaluable.event

import com.oasisfeng.invaluable.Dimensions
import com.oasisfeng.invaluable.Quantity
import com.oasisfeng.invaluable.actor.Actor
import com.oasisfeng.invaluable.actor.Credential
import com.oasisfeng.invaluable.actor.CredentialProof
import com.oasisfeng.invaluable.actor.CredentialProtocol
import com.oasisfeng.invaluable.actor.credentialMaterialOrNull
import com.oasisfeng.invaluable.data.Stream.OctetStream
import com.oasisfeng.invaluable.identity.Ed25519
import com.oasisfeng.invaluable.identity.Identity
import com.oasisfeng.invaluable.then
import kotlinx.io.bytestring.ByteString

/**
 * Adapter between Ed25519 keys and Link-backed credentials.
 *
 * Credential topology: `CredentialProtocol -> DATA -> 1 -> 3 -> 101 -> 112`, with a raw 32-octet RFC 8032 public
 * key as material. Proofs are raw 64-octet RFC 8032 signatures. The numeric OID is an external RFC 8410 reference;
 * SPKI and other transport containers are transient adapter views rather than graph identity.
 */
object Ed25519Credential {

    internal val protocol = CredentialProtocol(
        Dimensions.DATA().then(Quantity(1)).then(Quantity(3)).then(Quantity(101)).then(Quantity(112)))

    /** Build a credential from [identity]'s Ed25519 public key. */
    operator fun invoke(identity: Identity): Credential = fromRawPublicKey(identity.rawPublicKey())

    internal fun fromRawPublicKey(publicKey: ByteArray): Credential {
        require(publicKey.size == PUBLIC_KEY_SIZE) { "Ed25519 public key must be $PUBLIC_KEY_SIZE octets." }
        return Credential(protocol, OctetStream.encode(publicKey).link)
    }

    internal fun sign(identity: Identity, message: ByteArray): CredentialProof {
        val signature = identity.sign(message)
        check(signature.size == SIGNATURE_SIZE) { "Ed25519 signature must be $SIGNATURE_SIZE octets." }
        return CredentialProof(protocol, ByteString(*signature))
    }

    internal fun verify(credential: Credential, message: ByteArray, proof: CredentialProof): Boolean {
        if (proof.protocol != protocol) return false
        val publicKey = publicKeyOrNull(credential) ?: return false
        val signature = proof.payload.toByteArray()
        if (signature.size != SIGNATURE_SIZE) return false
        return try { Ed25519.verify(message, signature, publicKey.toSpki()) }
        catch (_: Exception) { false }
    }

    internal fun owns(credential: Credential, identity: Identity): Boolean
    = publicKeyOrNull(credential)?.contentEquals(identity.rawPublicKey()) == true

    /**
     * The X.509 SPKI public key of [actor]'s founding Ed25519 credential — the value [CapabilityWrap] boxes a
     * content-key to when disclosing dark content to [actor] (§20.3). Recovered from the actor's own link structure
     * (the credential material is embedded there), so no resolver is needed. Null if [actor] is not Ed25519-founded.
     */
    fun spkiOf(actor: Actor): ByteString? {
        val credential = Credential.render(actor.link.requireNestedTarget()) ?: return null
        return publicKeyOrNull(credential)?.let { ByteString(*it.toSpki()) }
    }

    internal fun publicKeyOrNull(credential: Credential): ByteArray? {
        val material = credentialMaterialOrNull(credential, protocol) ?: return null
        return OctetStream.render(material)?.toByteArray()?.takeIf { it.size == PUBLIC_KEY_SIZE }
    }

    private fun Identity.rawPublicKey(): ByteArray = publicKeySPKI.toRawPublicKey()

    private fun ByteArray.toRawPublicKey(): ByteArray {
        require(size == SPKI_PREFIX.size + PUBLIC_KEY_SIZE && SPKI_PREFIX.indices.all { this[it] == SPKI_PREFIX[it] })
        { "Identity public key is not an RFC 8410 Ed25519 SPKI value." }
        return copyOfRange(SPKI_PREFIX.size, size)
    }

    private fun ByteArray.toSpki() = SPKI_PREFIX + this.also { require(size == PUBLIC_KEY_SIZE) }

    private const val PUBLIC_KEY_SIZE = 32
    private const val SIGNATURE_SIZE = 64
    // RFC 8410 SubjectPublicKeyInfo prefix for id-Ed25519 followed by a 32-octet key.
    private val SPKI_PREFIX = byteArrayOf(0x30, 0x2a, 0x30, 0x05, 0x06, 0x03, 0x2b, 0x65, 0x70, 0x03, 0x21, 0x00)
}
